import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  createInstanceProductionChargeOverride,
  createGlobalProductionChargeRouting,
  fetchEffectiveProductionChargeRouting,
  fetchInstanceProductionChargeOverrides,
  fetchProductionDepartments,
  importInstanceProductionChargeRouting,
  previewInstanceProductionChargeRoutingImport,
  setProductionChargeRoutingMappingStatus,
  updateProductionChargeRoutingMapping,
} from "../../../../services/productionChargeRouting";

const SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "global", label: "Global Default" },
  { value: "instance-override", label: "Instance Override" },
  { value: "persisted", label: "Persisted" },
  { value: "none", label: "Unmapped" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive-override", label: "Inactive Override" },
  { value: "unmapped", label: "Unmapped" },
];

const IMPORT_ACTION_LABELS = {
  CREATE: "CREATE",
  SKIP: "SKIP - ALREADY CONFIGURED",
  UNMAPPED: "UNMAPPED",
  ERROR: "ERROR",
  UPDATE: "UPDATE",
  MATCH: "MATCH",
  MISMATCH: "MISMATCH",
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getSourceChipColor = (source) => {
  if (source === "instance-override") return "warning";
  if (source === "global") return "primary";
  if (source === "persisted") return "secondary";
  return "default";
};

const getStatusLabel = (row) => {
  if (row.source === "none") return "Unmapped";
  if (row.instanceOverride && row.instanceOverride.isActive === false) {
    return "Inactive Override";
  }
  return "Active";
};

const buildDepartmentFilterOptions = (departments) =>
  [{ id: "all", name: "All Departments" }].concat(
    departments.map((department) => ({
      id: Number(department.id),
      name: department.name,
    }))
  );

export default function ProductionChargeRoutingPanel({
  instanceId,
  canManage = false,
}) {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [chargeIdSearch, setChargeIdSearch] = useState("");
  const [chargeNameSearch, setChargeNameSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedScopeType, setSelectedScopeType] = useState("instance");
  const [saving, setSaving] = useState(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [importPreviewError, setImportPreviewError] = useState("");
  const [importScope, setImportScope] = useState("global");
  const [importFilter, setImportFilter] = useState("all");

  const loadAllRoutingData = useCallback(async () => {
    if (!instanceId) {
      return {
        effectiveRows: [],
        overrides: [],
        departmentRows: [],
      };
    }

    const [effectiveRows, overrides, departmentRows] = await Promise.all([
      fetchEffectiveProductionChargeRouting(instanceId),
      fetchInstanceProductionChargeOverrides(instanceId),
      fetchProductionDepartments(),
    ]);

    return {
      effectiveRows,
      overrides,
      departmentRows,
    };
  }, [instanceId]);

  const loadRows = useCallback(async () => {
    if (!instanceId) return;

    setLoading(true);
    setError("");
    try {
      const {
        effectiveRows,
        overrides,
        departmentRows,
      } = await loadAllRoutingData();

      const overridesByChargeId = new Map(
        overrides.map((row) => [Number(row.chargeId), row])
      );
      setRows(
        (Array.isArray(effectiveRows) ? effectiveRows : []).map((row) => ({
          ...row,
          instanceOverride:
            overridesByChargeId.get(Number(row.chargeId)) || row.instanceOverride || null,
        }))
      );
      setDepartments(Array.isArray(departmentRows) ? departmentRows : []);
    } catch (loadError) {
      const message =
        loadError?.response?.data?.message ||
        "Unable to load Production Charge Routing.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [instanceId, loadAllRoutingData]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const departmentOptions = useMemo(
    () => buildDepartmentFilterOptions(departments),
    [departments]
  );

  const filteredRows = useMemo(() => {
    const normalizedChargeIdSearch = normalizeText(chargeIdSearch);
    const normalizedChargeNameSearch = normalizeText(chargeNameSearch);

    return rows.filter((row) => {
      if (
        normalizedChargeIdSearch &&
        !String(row.chargeId || "").includes(normalizedChargeIdSearch)
      ) {
        return false;
      }

      if (
        normalizedChargeNameSearch &&
        !normalizeText(row.chargeName).includes(normalizedChargeNameSearch)
      ) {
        return false;
      }

      if (
        departmentFilter !== "all" &&
        Number(row.departmentId || 0) !== Number(departmentFilter)
      ) {
        return false;
      }

      if (sourceFilter !== "all" && row.source !== sourceFilter) {
        return false;
      }

      if (statusFilter === "active" && getStatusLabel(row) !== "Active") {
        return false;
      }
      if (
        statusFilter === "inactive-override" &&
        getStatusLabel(row) !== "Inactive Override"
      ) {
        return false;
      }
      if (statusFilter === "unmapped" && getStatusLabel(row) !== "Unmapped") {
        return false;
      }

      return true;
    });
  }, [
    chargeIdSearch,
    chargeNameSearch,
    departmentFilter,
    rows,
    sourceFilter,
    statusFilter,
  ]);

  const openEditor = (row) => {
    const ownedMapping = row.instanceOverride || row.globalMapping || null;
    setEditingRow(row);
    setSelectedDepartmentId(
      Number(ownedMapping?.departmentId || row.departmentId || "") || ""
    );
    setSelectedScopeType(ownedMapping?.scopeType || "instance");
    setEditorOpen(true);
  };

  const closeEditor = (force = false) => {
    if (saving && !force) return;
    setEditorOpen(false);
    setEditingRow(null);
    setSelectedDepartmentId("");
    setSelectedScopeType("instance");
  };

  const handleSave = async () => {
    if (!editingRow || !selectedDepartmentId) {
      toast.error("Choose a Production Department.");
      return;
    }

    setSaving(true);
    try {
      const ownedMapping = editingRow.instanceOverride || editingRow.globalMapping || null;
      if (ownedMapping?.id) {
        await updateProductionChargeRoutingMapping(ownedMapping.id, {
          departmentId: Number(selectedDepartmentId),
          scopeType: selectedScopeType,
          instanceId: selectedScopeType === "instance" ? Number(instanceId) : null,
          isActive: true,
        });
        toast.success(
          ownedMapping.isActive === false
            ? "Production charge routing override reactivated."
            : "Production charge routing override updated."
        );
      } else {
        const createRouting = selectedScopeType === "global"
          ? createGlobalProductionChargeRouting
          : (payload) => createInstanceProductionChargeOverride(instanceId, payload);
        await createRouting({
          chargeId: Number(editingRow.chargeId),
          chargeName: editingRow.chargeName,
          departmentId: Number(selectedDepartmentId),
          scopeType: selectedScopeType,
        });
        toast.success("Production charge routing override created.");
      }

      closeEditor(true);
      await loadRows();
    } catch (saveError) {
      toast.error(
        saveError?.response?.data?.message ||
          "Unable to save the Production charge routing override."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (row) => {
    if (!row.instanceOverride?.id) return;
    const confirmed = window.confirm(
      "Revert this charge to the inherited Global Default for this company?"
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await setProductionChargeRoutingMappingStatus(row.instanceOverride.id, false);
      toast.success("Production charge routing reverted to the Global Default.");
      await loadRows();
    } catch (revertError) {
      toast.error(
        revertError?.response?.data?.message ||
          "Unable to revert the Production charge routing override."
      );
    } finally {
      setSaving(false);
    }
  };

  const previewCharge131Record = useMemo(
    () =>
      importSummary?.records?.find(
        (record) => Number(record.chargeId) === 131
      ) || null,
    [importSummary]
  );

  const importRegressionWarning = useMemo(() => {
    if (!previewCharge131Record) return "";
    if (previewCharge131Record.action === "ERROR") {
      return "Charge 131 expected Engraving from the legacy map but encountered an import error. Other valid rows can still be imported.";
    }
    if (previewCharge131Record.action === "UNMAPPED") {
      return "Charge 131 expected Engraving from the legacy map but is currently unmapped. Other valid rows can still be imported.";
    }
    if (previewCharge131Record.action === "MISMATCH") {
      return "Charge 131 expected Engraving from the legacy map but currently resolves differently. It will not be overwritten by the safe import.";
    }
    if (
      normalizeText(previewCharge131Record.sourceDepartmentKey) !== "engraving" ||
      normalizeText(previewCharge131Record.resolvedDepartmentName) !== "engraving"
    ) {
      return "Charge 131 expected Engraving from the legacy map but currently resolves differently. Other valid rows can still be imported.";
    }
    return "";
  }, [previewCharge131Record]);

  const openImportPreview = async () => {
    if (!instanceId) {
      toast.error("This company instance is still loading.");
      return;
    }

    const defaultScope = "global";
    setImportScope(defaultScope);
    setImportFilter("all");
    setImportPreviewLoading(true);
    setImportPreviewOpen(true);
    setImportSummary(null);
    setImportPreviewError("");
    try {
      const summary = await previewInstanceProductionChargeRoutingImport(instanceId, {
        scopeType: defaultScope,
      });
      setImportSummary(summary);
    } catch (previewError) {
      const message =
        previewError?.response?.data?.message ||
        "Unable to preview the existing charge map import.";
      toast.error(message);
      setImportPreviewError(message);
      setImportSummary(null);
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const handleImportScopeChange = async (scopeType) => {
    setImportScope(scopeType);
    setImportPreviewLoading(true);
    setImportSummary(null);
    setImportPreviewError("");
    try {
      setImportSummary(await previewInstanceProductionChargeRoutingImport(instanceId, { scopeType }));
    } catch (previewError) {
      const message = previewError?.response?.data?.message || "Unable to preview the existing charge map.";
      toast.error(message);
      setImportPreviewError(message);
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const closeImportPreview = () => {
    if (importPreviewLoading || importing) return;
    setImportPreviewOpen(false);
    setImportSummary(null);
    setImportPreviewError("");
  };

  const handleImport = async () => {
    if (!instanceId) return;
    setImporting(true);
    try {
      const summary = await importInstanceProductionChargeRouting(instanceId, {
        scopeType: importScope,
      });
      setImportSummary(summary);
      await loadRows();
      toast.success(
        `Imported Production charge routing. Created: ${summary?.created || 0}, Skipped Existing: ${summary?.skipped || 0}, Unmapped: ${summary?.unmapped || 0}, Errors: ${summary?.errors?.length || 0}.`
      );
    } catch (importError) {
      toast.error(
        importError?.response?.data?.message ||
          "Unable to import the existing charge map for this company."
      );
    } finally {
      setImporting(false);
    }
  };

  const importCounts = useMemo(
    () => ({
      create: Number(importSummary?.created || 0),
      skipped: Number(importSummary?.skipped || 0),
      matched: Number(importSummary?.matched || 0),
      mismatched: Number(importSummary?.mismatched || 0),
      unmapped: Number(importSummary?.unmapped || 0),
      errors: Array.isArray(importSummary?.errors) ? importSummary.errors.length : 0,
    }),
    [importSummary]
  );
  const importableRecordCount = useMemo(
    () => (importSummary?.records || []).filter((record) => record.action === "CREATE").length,
    [importSummary]
  );

  const filteredImportRecords = useMemo(() => (importSummary?.records || []).filter((record) => {
    if (importFilter === "all") return true;
    if (importFilter === "unmapped") return record.action === "UNMAPPED" || (!record.currentGlobalDepartmentId && !record.currentInstanceDepartmentId);
    if (importFilter === "matches") return record.action === "MATCH";
    if (importFilter === "mismatches") return record.action === "MISMATCH";
    if (importFilter === "global") return Boolean(record.currentGlobalDepartmentId);
    if (importFilter === "instance") return Boolean(record.currentInstanceDepartmentId);
    return [record.resolvedDepartmentName, record.currentGlobalDepartmentName, record.currentInstanceDepartmentName]
      .some((name) => normalizeText(name) === importFilter);
  }), [importFilter, importSummary]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Stack spacing={0.5}>
        <Typography variant="h6">Production Charge Routing</Typography>
        <Typography variant="body2" color="text.secondary">
          Control which Production Department each decoration charge routes to for this
          company. Global defaults are inherited automatically unless you create an override.
        </Typography>
      </Stack>

      <Alert severity="info">
        Global defaults apply to every company. Instance overrides apply only to this company.
      </Alert>

      {!canManage ? (
        <Alert severity="warning">
          You can review effective Production charge routing, but only administrators for
          this company can create or edit overrides.
        </Alert>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {canManage ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Import Existing Charge Map
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Migrate the legacy charge-routing JSON into model-backed routing records.
                Imports default to canonical Global Defaults.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={openImportPreview}
              disabled={loading || importPreviewLoading || !instanceId}
            >
              Import Existing Charge Map
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
        >
          <TextField
            label="Search Charge ID"
            size="small"
            value={chargeIdSearch}
            onChange={(event) => setChargeIdSearch(event.target.value)}
          />
          <TextField
            label="Search Charge Name"
            size="small"
            value={chargeNameSearch}
            onChange={(event) => setChargeNameSearch(event.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="routing-department-filter-label">Department</InputLabel>
            <Select
              labelId="routing-department-filter-label"
              label="Department"
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
            >
              {departmentOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="routing-source-filter-label">Source</InputLabel>
            <Select
              labelId="routing-source-filter-label"
              label="Source"
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
            >
              {SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="routing-status-filter-label">Status</InputLabel>
            <Select
              labelId="routing-status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Charge ID</TableCell>
              <TableCell>Charge Name</TableCell>
              <TableCell>Effective Department</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Effective Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">
                      Loading Production Charge Routing...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No Production charge routing rows match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading
              ? filteredRows.map((row) => (
                  <TableRow key={row.chargeId}>
                    <TableCell>{row.chargeId}</TableCell>
                    <TableCell>{row.chargeName}</TableCell>
                    <TableCell>
                      {row.departmentName || (
                        <Typography variant="body2" color="text.secondary">
                          Unmapped
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.scopeType === "global" ? "Global" : row.scopeType === "instance" ? "Instance" : "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.sourceLabel}
                        color={getSourceChipColor(row.source)}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>{getStatusLabel(row)}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {canManage ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openEditor(row)}
                          >
                            {row.instanceOverride?.id || row.globalMapping?.id
                              ? "Edit Owned Routing"
                              : "Override for this Company"}
                          </Button>
                        ) : null}
                        {canManage && row.instanceOverride?.id && row.instanceOverride.isActive !== false ? (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => handleRevert(row)}
                          >
                            Revert to Global Default
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editorOpen} onClose={closeEditor} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRow?.instanceOverride?.id || editingRow?.globalMapping?.id
            ? "Edit Owned Production Charge Routing"
            : "Override Production Charge Routing"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Existing Sales Order snapshots and scheduled production work may retain their
              previous routing. New edits and regenerated Sales Orders will use this updated
              routing.
            </Alert>

            <TextField
              label="Charge ID"
              size="small"
              value={editingRow?.chargeId || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Charge Name"
              size="small"
              value={editingRow?.chargeName || ""}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Inherited Source"
              size="small"
              value={editingRow?.sourceLabel || ""}
              InputProps={{ readOnly: true }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="override-department-label">Production Department</InputLabel>
              <Select
                labelId="override-department-label"
                label="Production Department"
                value={selectedDepartmentId}
                onChange={(event) => setSelectedDepartmentId(event.target.value)}
              >
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="routing-availability-label">Availability</InputLabel>
              <Select
                labelId="routing-availability-label"
                label="Availability"
                value={selectedScopeType}
                onChange={(event) => setSelectedScopeType(event.target.value)}
              >
                <MenuItem value="instance">Instance Only</MenuItem>
                <MenuItem value="global">Global</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importPreviewOpen}
        onClose={closeImportPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Import Existing Charge Map</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Compare the legacy charge map with current Global and Instance database routing.
              Mismatches are never overwritten by this import without an explicit overwrite action.
            </Alert>
            <FormControl size="small" fullWidth sx={{ maxWidth: 360, minWidth: 280 }}>
              <InputLabel id="legacy-import-scope-label">Import As</InputLabel>
              <Select
                labelId="legacy-import-scope-label"
                id="legacy-import-scope"
                label="Import As"
                value={importScope}
                onChange={(event) => handleImportScopeChange(event.target.value)}
                renderValue={(value) =>
                  value === "instance" ? "This Instance Only" : "Global Default"
                }
              >
                <MenuItem value="global">Global Default</MenuItem>
                <MenuItem value="instance">This Instance Only</MenuItem>
              </Select>
              <FormHelperText>
                {importScope === "global"
                  ? "Creates the canonical Production routing inherited by all companies without an override."
                  : "Creates routing only for this company."}
              </FormHelperText>
            </FormControl>
            <Alert severity="warning">
              Importing routing records does not rewrite historical Sales Orders, Production
              Work Orders, scheduled work, Purchase Orders, or snapshots by itself.
            </Alert>

            {importRegressionWarning ? (
              <Alert severity="warning">{importRegressionWarning}</Alert>
            ) : null}

            {importPreviewLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">
                  Previewing the existing charge map for this company...
                </Typography>
              </Stack>
            ) : null}

            {!importPreviewLoading && importPreviewError ? (
              <Alert severity="error">
                Unable to preview legacy charge routing: {importPreviewError}
              </Alert>
            ) : null}

            {importSummary ? (
              <>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip label={`Legacy mappings found: ${(importSummary.records || []).length}`} />
                  <Chip label={`Will Create: ${importCounts.create}`} color="primary" />
                  <Chip label={`Already Configured: ${importCounts.skipped}`} />
                  <Chip label={`Matches: ${importCounts.matched}`} color="success" variant="outlined" />
                  <Chip label={`Mismatches: ${importCounts.mismatched}`} color="warning" />
                  <Chip label={`Unmapped: ${importCounts.unmapped}`} variant="outlined" />
                  <Chip label={`Errors: ${importCounts.errors}`} color="error" variant="outlined" />
                </Stack>

                {(importSummary.records || []).length === 0 ? (
                  <Alert severity="info">The legacy charge-routing map contains no previewable records.</Alert>
                ) : null}

                <FormControl size="small" sx={{ maxWidth: 240 }}>
                  <InputLabel id="legacy-import-filter-label">Filter</InputLabel>
                  <Select
                    labelId="legacy-import-filter-label"
                    label="Filter"
                    value={importFilter}
                    onChange={(event) => setImportFilter(event.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unmapped">Unmapped</MenuItem>
                    <MenuItem value="matches">Matches</MenuItem>
                    <MenuItem value="mismatches">Mismatches</MenuItem>
                    <MenuItem value="global">Global</MenuItem>
                    <MenuItem value="instance">Instance</MenuItem>
                    <MenuItem value="engraving">Engraving</MenuItem>
                    <MenuItem value="awards">Awards</MenuItem>
                  </Select>
                </FormControl>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Charge ID</TableCell>
                        <TableCell>Charge Name</TableCell>
                        <TableCell>Legacy Department</TableCell>
                        <TableCell>Current Global Department</TableCell>
                        <TableCell>Current Instance Override</TableCell>
                        <TableCell>Proposed Department</TableCell>
                        <TableCell>Target Scope</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredImportRecords.map((record) => (
                        <TableRow key={`${record.chargeId}-${record.sourceDepartmentKey}`}>
                          <TableCell>{record.chargeId}</TableCell>
                          <TableCell>{record.chargeName || "-"}</TableCell>
                          <TableCell>{record.resolvedDepartmentName || record.sourceDepartmentKey || "-"}</TableCell>
                          <TableCell>{record.currentGlobalDepartmentName || "-"}</TableCell>
                          <TableCell>{record.currentInstanceDepartmentName || "-"}</TableCell>
                          <TableCell>{record.resolvedDepartmentName || "-"}</TableCell>
                          <TableCell>
                            {importSummary.scopeType === "instance" ? "Instance" : "Global"}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2">
                                {IMPORT_ACTION_LABELS[record.action] || record.action || "-"}
                              </Typography>
                              {record.reason ? (
                                <Typography variant="caption" color="text.secondary">
                                  {record.reason}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportPreview} disabled={importPreviewLoading || importing}>
            Close
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={
              importPreviewLoading ||
              importing ||
              !importSummary ||
              Boolean(importPreviewError) ||
              importableRecordCount === 0
            }
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
